import moment from 'moment';
// 使用numeral数值格式化库
import Numeral from 'numeral';

// 统一非数字、空数据处理方法
// 不能用lodash 的isEmpty判断，loadsh的isEmpty会把0判断为空
export const isEmptyValue = (value: string | number) => {
  if (value === null || value === undefined || (typeof value === 'string' && value?.trim() === ''))
    return true;
  return false;
};

// 根据传入的字符个数和字符，返回对应个数字符连接的字符串
const getNumStrings = (strNum: number, text: string) => {
  let numStr = '';
  let numb = 0;
  while (numb < strNum) {
    numStr = numStr + text;
    numb += 1;
  }
  return numStr;
};

// 获取numeral格式保留小数位字符串方法，如 '0.00',并判断是否需要补0
const getDecimalNum = (num: number, fillZero: boolean) => {
  let frontStr = '0.';
  const ZEROSTR = '0';
  const numTemp = Number(num);
  if (numTemp <= 0) return ZEROSTR;
  const numStr = getNumStrings(numTemp, ZEROSTR);

  // 若小数位不够，不需要补0
  if (!fillZero) {
    frontStr = `${frontStr}[${numStr}]`;
  } else {
    frontStr = frontStr + numStr;
  }
  return frontStr;
};

// 判断是否大于9999、999
export const moreThanMaxNum = (
  value: string | number,
  frontNum: number,
  backNum = 0,
  isPercent = true,
) => {
  const dataNumTemp = Number(value);
  const dataNum = isPercent ? dataNumTemp : dataNumTemp * 100;
  // 增加小于9999、999的情况
  const isNegative = dataNum < 0;
  const STRNINE = '9';
  const frontStr = getNumStrings(frontNum, STRNINE);
  const backStr = getNumStrings(backNum, STRNINE);
  const valueStr = backNum > 0 ? `${frontStr}.${backStr}` : frontStr;
  const formattedValueStr = isNegative ? `-${valueStr}` : valueStr;
  const valueNum = Number(formattedValueStr);

  return {
    isMoreThan: isNegative ? dataNum < valueNum : dataNum > valueNum,
    moreStr: formattedValueStr,
  };
};

// 通用numeral数值格式化库步骤
const getNumeralValue = (
  data: string | number,
  emptyValue: string | number,
  formatValue: string,
  isPercent = false,
) => {
  // 筛选空值,空值返回指定内容
  if (isEmptyValue(data)) return emptyValue;
  // 非数字值的特殊值,返回原值
  const dataNum = Number(data);
  if (isNaN(dataNum)) return data;
  // 获取Numeral对应格式化数据
  const dataValue = `${Numeral(dataNum).format(formatValue)}${isPercent ? '%' : ''}`;
  return dataValue;
};

/**
 * 数字格式化小数位（保留几位小数，数位不够需补0）
 * data：传入的数字 number或者字符串number
 * num：保留的小数位数，默认0
 * fillZero：位数不够是否填充0，默认false
 * emptyValue: 无数据时如 '' null undefined，需要返回的内容，如 '' '-' 0 等,默认为空 '' */
export const decimalFormat = (
  data: string | number,
  num = 0,
  fillZero = false,
  emptyValue: string | number = '',
  frontNum = 0,
) => {
  // 筛选空值,空值返回指定内容
  if (isEmptyValue(data)) return emptyValue;
  // 非数字值的特殊值,返回原值
  const dataNum = Number(data);
  if (isNaN(dataNum)) return data;

  // 如果有最大限定数，先判断是否超出限度
  if (frontNum > 0) {
    const moreObj = moreThanMaxNum(data, frontNum, num);
    // 如果超过最大限定数,直接返回最大限制数
    if (moreObj.isMoreThan) {
      return moreObj.moreStr;
    }
  }

  // 获取numeral保留小数位数格式化字符串
  const formatStr = getDecimalNum(num, fillZero);
  // 获取Numeral转化格式化值
  return getNumeralValue(data, emptyValue, formatStr);
};

/**
 * 数字格式化成BMK
 * data：传入的数字 number或者字符串number
 * num：保留的小数位数，默认0
 * fillZero：位数不够是否填充0，默认false
 * emptyValue: 无数据时如 '' null undefined，需要返回的内容，如 '' '-' 0 等,默认为空 ''
 * */
export const financeFormat = (
  data: string | number,
  num = 0,
  fillZero = false,
  emptyValue: string | number = '',
) => {
  // 获取numeral保留小数位数格式化字符串
  const formatStr = getDecimalNum(num, fillZero);
  // 转BMK对应格式化字符串
  const formatValue = `${formatStr}a`;

  // 筛选空值,空值返回指定内容
  if (isEmptyValue(data)) return emptyValue;
  // 非数字值的特殊值,返回原值
  const dataNum = Number(data);
  if (isNaN(dataNum)) return data;

  // 获取Numeral对应格式化数据
  const bmkValue = `${Numeral(dataNum).format(formatValue)}`;
  // 获取返回值最后一位字符串
  const lastChar = bmkValue[bmkValue.length - 1];
  // 判断是否是小写字母，是，则转为大写
  const lastCharUpper = /[a-z]/.test(lastChar) ? lastChar?.toUpperCase() : lastChar;
  const BMKValue = bmkValue?.slice(0, -1) + lastCharUpper;
  return BMKValue;
};

/**
 * 数字格式化千位分隔符,以,号隔开
 * data：传入的数字 number或者字符串number
 * num：保留的小数位数，默认0
 * fillZero：位数不够是否填充0，默认false
 * emptyValue: 无数据时如 '' null undefined，需要返回的内容，如 '' '-' 0 等,默认为空 ''
 * */
export const thousandthFormat = (
  data: string | number,
  num = 0,
  fillZero = false,
  emptyValue: string | number = '',
) => {
  // 获取numeral保留小数位数格式化字符串
  const formatStr = getDecimalNum(num, fillZero);
  // 转千位格式化字符串
  const formatValue = `0,${formatStr}`;
  // 获取Numeral转化格式化值
  return getNumeralValue(data, emptyValue, formatValue);
};

/**
 * 环比格式化
 * data: number或者字符串number
 * num：保留的小数位数，默认0
 * fillZero：位数不够是否填充0，默认false
 * emptyValue: 无数据时如 '' null undefined，需要返回的内容，如 '' '-' 0 等,默认为空 ''
 * isPercent: 传入数据是否是已转为百分比的数字
 * */
export const ratioValueFormat = (
  data: string | number,
  num = 0,
  fillZero = false,
  emptyValue: any = '',
  isPercent = false,
  frontNum = 4,
) => {
  // 筛选空值,空值返回指定内容
  if (isEmptyValue(data)) return emptyValue;
  // 非数字值的特殊值,返回原值
  const dataNum = Number(data);
  if (isNaN(dataNum)) return data;

  const moreObj = moreThanMaxNum(data, frontNum, num, isPercent);
  const ENDSTR = '%+';
  // 如果超过最大限定数，直接返回最大限制数
  if (moreObj.isMoreThan) {
    return moreObj.moreStr + ENDSTR;
  }
  // 获取numeral保留小数位数格式化字符串
  const formatStr = getDecimalNum(num, fillZero);
  // 转百分数格式化字符串
  const formatValue = `${formatStr}%`;
  // 若已经是*100的数值，则进行小数处理后，直接加%,若为空值，返回空值对象，并不添加%
  if (isPercent) {
    return getNumeralValue(data, emptyValue, formatStr, isPercent);
  }
  // 获取Numeral转化格式化值
  return getNumeralValue(data, emptyValue, formatValue);
};

/**
 * 提取html字符串中的纯文本信息
 * @param html html字符串
 * @returns html字符串的纯文本
 */
export const extractTextFromHtml = (html?: string): string => {
  if (typeof html !== 'string') return '';
  return html.replace(/(<\/?[^>]*>)|(\s*&[a-zA-Z]+;\s*)/g, '');
};

/**
 * 日期格式化
 * date：传入的日期数据
 * type: 需要转化的日期格式类型，范围hours/day/month/year
 */
export const dateAllFormat = (date: string, type: string) => {
  if (!date) return '';
  let formatStr = '';
  switch (type) {
    case 'hours':
      formatStr = 'YYYY-MM-DD HH:mm:ss';
      break;
    case 'day':
      formatStr = 'YYYY-MM-DD';
      break;
    case 'month':
      formatStr = 'YYYY-MM';
      break;
    case 'year':
      formatStr = 'YYYY';
      break;
  }
  return moment(date).format(formatStr);
};

/**
 * 根据给定的条件选择返回两个值中的一个。
 * @param {boolean} condition - 决定返回哪个值的条件。
 * @param {any} valueIfTrue - 当条件为真时返回的值。
 * @param {any} valueIfFalse - 当条件为假时返回的值。
 *  @param {boolean} isUseDefualt - 当条件为假时返回的值。
 * @returns any - 根据条件选择的值。
 */
export const selectValByCondition = (
  condition: boolean,
  valueIfTrue?: any,
  valueIfFalse?: any,
  isUseDefualt = true,
): any => {
  if (isUseDefualt) {
    return condition ? valueIfTrue || valueIfFalse : valueIfFalse || valueIfTrue;
  }
  return condition ? valueIfTrue : valueIfFalse;
};

// selectValByCondition 别名
export const getLanguage = selectValByCondition;

// selectValByCondition 别名
export const getName = selectValByCondition;

// 将数据中所有全角改为半角
export const changeParentheses = (str: string): string => {
  let result = str?.replace(/（/g, '(');
  result = result?.replace(/）/g, ')');
  return result || '';
};

export const DATA_TYPE_OBJECT: any = {
  daily: 'Day',
  weekly: 'Week',
  monthly: 'Month',
};
